import { mergeStyleSets, Nav } from "@fluentui/react";
import { AppSettingsType } from "AppSettings";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { TimelineType, UserRole } from "types/types";
import { useCurrentRoute } from "../../../config/routes";
import { useSelector } from "../../../store/hooks";
import * as VehicleSlice from "../../../store/vehicle";

const getClasses = (theme, isDarkMode) =>
  mergeStyleSets({
    navigation: {
      borderBottom: `1px solid ${theme.palette.neutralLight}`,
      height: "100%",
      "& .ms-Nav-groupContent": {
        marginBottom: "0",
      },
      ".ms-Nav-compositeLink.is-selected .ms-Button": {
        background: isDarkMode ? "#001321" : "#d0e7f8",
      },
    },
  });

// Bookings and history replace the form content instead of being a section of
// it, so their element does not exist yet when the tab is selected.
const FULL_PAGE_TABS = [
  "booking-vehicle-form",
  "history-table",
  "attachments-table",
];

const VEHICLE_FORM_TABS = [
  "general-vehicle-form",
  "vehicle-form",
  "finance-vehicle-form",
  ...FULL_PAGE_TABS,
];

// How long a deep link keeps correcting itself while the form content settles.
const SETTLE_MS = 3000;

// A plain "scroll" listener would also catch our own scrolling, so user intent
// is detected from the input events instead.
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown"];

/**
 * Scrolls the vehicle form to the section of the given tab.
 *
 * The section's `offsetTop` can't be used: its offsetParent is an inner layout
 * div (`position: relative`) and not the scroll container, so the value misses
 * everything above that div (navigation, main fields, container margin) and the
 * scroll lands short of the section. Measuring both rects against the scroll
 * container stays correct no matter how the sections above are structured.
 */
const scrollToTab = (key: string) => {
  const wrapper = document.querySelector("#vehicle-form-wrapper");
  if (!wrapper) return;

  if (FULL_PAGE_TABS.includes(key)) {
    wrapper.scroll({ top: 0, left: 0, behavior: "auto" });
    return;
  }

  const section = document.getElementById(key);
  if (!section) return;

  // Keep the section heading clear of the sticky navigation bar.
  const stickyNavHeight =
    (document.querySelector("#vehicle-form-navigation") as HTMLElement)
      ?.offsetHeight ?? 0;
  const top =
    section.getBoundingClientRect().top -
    wrapper.getBoundingClientRect().top +
    wrapper.scrollTop -
    stickyNavHeight;

  wrapper.scroll({ top: Math.max(top, 0), left: 0, behavior: "auto" });
};

const SidebarNav = () => {
  const dispatch = useDispatch();
  const userRole = useSelector((s) => s.auth.userRole);
  const route = useCurrentRoute();
  const tab = useSelector((s) => s.vehicle.tab);
  const vehicle = useSelector((s) => s.vehicle.vehicle);
  const theme = useTheme() as IStyledTheme;
  const isDarkMode = theme.isDark;
  const classes = getClasses(theme, isDarkMode);
  const { t } = useTranslation();
  const location = useLocation();
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  const isFinanceVisible = !(
    route === "createVehicle" && userRole === UserRole.external
  );

  const isVehicleVisible =
    route !== "createTradeVehicle" && !vehicle?.isTradeLicensePlate;

  const isBookingsVisible = vehicleAppSettings?.VEHICLE_TIMELINE_TYPES.includes(
    TimelineType.VEHICLE_PREMIUM_BOOKINGS
  );

  const isHistoryVisible = vehicleAppSettings?.HAS_VEHICLE_HISTORY;
  const isAttachmentsVisible = vehicleAppSettings?.HAS_VEHICLE_ATTACHMENTS;

  const handleNavItemClick = (e, tab) => {
    if (!tab) return;
    dispatch(VehicleSlice.setTab(tab.key as any));
    scrollToTab(tab.key);
  };

  useEffect(() => {
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    if (!tabFromUrl || !VEHICLE_FORM_TABS.includes(tabFromUrl)) return;

    dispatch(VehicleSlice.setTab(tabFromUrl));

    const wrapper = document.querySelector("#vehicle-form-wrapper");
    const content = wrapper?.firstElementChild;
    if (!wrapper || !content) return;

    // Unlike a nav click, a deep link runs while the premium lines are still
    // empty and waiting for their data. The content is then shorter than the
    // target position, so the browser clamps the scroll and we land above the
    // section - and stay there once the lines fill in. Keep re-applying it
    // while the content grows, and hand control back to the user as soon as
    // they scroll themselves or the layout has settled.
    let active = true;
    const apply = () => {
      if (active) scrollToTab(tabFromUrl);
    };
    const release = () => {
      if (!active) return;
      active = false;
      observer.disconnect();
      clearTimeout(settleTimeout);
      USER_SCROLL_EVENTS.forEach((event) =>
        wrapper.removeEventListener(event, release)
      );
    };

    // getBoundingClientRect in scrollToTab flushes layout, so no need to wait
    // for a frame before the first attempt.
    const observer = new ResizeObserver(apply);
    const settleTimeout = window.setTimeout(release, SETTLE_MS);

    apply();
    observer.observe(content);
    USER_SCROLL_EVENTS.forEach((event) =>
      wrapper.addEventListener(event, release, { passive: true })
    );

    return release;
  }, [location.search, dispatch]);

  return (
    <>
      <Nav
        onLinkClick={handleNavItemClick}
        selectedKey={tab}
        groups={getNavLinkGroups(
          isFinanceVisible,
          isVehicleVisible,
          isBookingsVisible,
          isHistoryVisible,
          isAttachmentsVisible,
          t,
          vehicleAppSettings,
          route
        )}
        className={classes.navigation}
      />
    </>
  );
};

const getNavLinkGroups = (
  isFinanceVisible: boolean,
  isVehicleVisible: boolean,
  isBookingsVisible: boolean,
  isHistoryVisible: boolean,
  isAttachmentsVisible: boolean,
  t: any,
  vehicleAppSettings: AppSettingsType,
  route: any
) => {
  const groups = [
    {
      links: [
        {
          name: t("bfm.vehicleForm.tabGeneral.label"),
          url: "",
          key: "general-vehicle-form",
          isExpanded: true,
          target: "_blank",
        },
      ],
    },
  ];

  if (isVehicleVisible) {
    groups[0].links.push({
      name: t("bfm.vehicleForm.tabVehicle.label"),
      url: "",
      key: "vehicle-form",
      isExpanded: true,
      target: "_blank",
    });
  }

  if (isFinanceVisible) {
    groups[0].links.push({
      name: t("bfm.vehicleForm.tabFinancials.label"),
      url: "",
      key: "finance-vehicle-form",
      isExpanded: true,
      target: "_blank",
    });
  }

  if (
    isBookingsVisible &&
    route !== "createVehicle" &&
    route !== "editTempVehicle" &&
    route !== "createTradeVehicle"
  ) {
    groups[0].links.push({
      name: t("bfm.bookings.label"),
      url: "",
      key: "booking-vehicle-form",
      isExpanded: true,
      target: "_blank",
    });
  }

  // Add History nav item
  if (
    isHistoryVisible &&
    route !== "createVehicle" &&
    route !== "editTempVehicle" &&
    route !== "createTradeVehicle"
  ) {
    groups[0].links.push({
      name: t("bfm.vehicle.tabs.history"),
      url: "",
      key: "history-table",
      isExpanded: true,
      target: "_blank",
    });
  }

  // Documents are stored per saved vehicle only
  if (
    isAttachmentsVisible &&
    route !== "createVehicle" &&
    route !== "editTempVehicle" &&
    route !== "createTradeVehicle"
  ) {
    groups[0].links.push({
      name: t("bfm.vehicle.tabs.attachments"),
      url: "",
      key: "attachments-table",
      isExpanded: true,
      target: "_blank",
    });
  }

  return groups;
};
export default SidebarNav;
